import { ApiPath } from '@common/app/api-path';
import { type UserFactory } from '@common/types/factoryType';
import {
  type ClaimAllMaterialsResponse,
  type ClaimMaterialByIdResponse,
  type ClearFactoryProductionResponse,
  type GetFactoriesListResponse,
  type PutToFactoryRequestType,
} from '@common/types/query/factory';

import { type Storage } from '@/common/types/userType';

import { api } from '../base';

const factoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getFactories: builder.query<GetFactoriesListResponse, string | undefined>({
      query: () => ApiPath.GET_FACTORIES,
      providesTags: ['BOOST', 'FACTORY'],
      keepUnusedDataFor: 0,
    }),
    buildFactory: builder.mutation<UserFactory[], { id: string }>({
      query: ({ id }) => ({
        url: ApiPath.BUILD_FACTORY + id,
        method: 'POST',
      }),
      invalidatesTags: ['FACTORY'],
    }),
    upgradeFactory: builder.mutation<UserFactory, { id: string }>({
      query: ({ id }) => ({
        url: ApiPath.BUILD_FACTORY + id,
        method: 'PATCH',
      }),
      invalidatesTags: ['FACTORY'],
    }),
    claimAllMaterials: builder.mutation<
      ClaimAllMaterialsResponse,
      void | boolean
    >({
      query: () => ({
        url: ApiPath.CLAIM_ALL_MATERIALS,
        method: 'POST',
      }),
    }),
    claimMaterialById: builder.mutation<
      ClaimMaterialByIdResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: ApiPath.CLAIM_MATERIAL + id,
        method: 'POST',
      }),
    }),
    clearFactoryProduction: builder.mutation<
      ClearFactoryProductionResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: ApiPath.FACTORY + `/${id}/clear`,
        method: 'POST',
      }),
    }),
    putToFactory: builder.mutation<UserFactory, PutToFactoryRequestType>({
      query: (body) => ({
        url: ApiPath.FACTORY,
        method: 'PUT',
        body,
      }),
    }),
    putAllFactory: builder.mutation<
      { plants: UserFactory[]; storage: Storage },
      void
    >({
      query: () => ({
        url: ApiPath.FACTORY + '/load/all',
        method: 'PUT',
      }),
    }),
  }),
});

const {
  usePutAllFactoryMutation,
  useLazyGetFactoriesQuery,
  useGetFactoriesQuery,
  useBuildFactoryMutation,
  useClaimAllMaterialsMutation,
  useUpgradeFactoryMutation,
  useClaimMaterialByIdMutation,
  usePutToFactoryMutation,
  useClearFactoryProductionMutation,
} = factoryApi;

export {
  factoryApi,
  useBuildFactoryMutation,
  useClaimAllMaterialsMutation,
  useClaimMaterialByIdMutation,
  useClearFactoryProductionMutation,
  useGetFactoriesQuery,
  useLazyGetFactoriesQuery,
  usePutAllFactoryMutation,
  usePutToFactoryMutation,
  useUpgradeFactoryMutation,
};
